<script setup lang="ts">
import { computed } from 'vue'

import { useDevice } from '#imports'
import { useModal } from '~/composables/common/use-modal'
import { useModalStore } from '~/stores/modal.store.ts'

const props = defineProps({
  modalId: {
    type: String,
    required: false,
    default: null
  },
  modalClass: {
    type: String,
    required: false,
    default: ''
  },
  modalDialogClasses: {
    type: String,
    required: false,
    default: ''
  },
  modalContentClasses: {
    type: String,
    required: false,
    default: ''
  },
  modalBodyClasses: {
    type: String,
    required: false,
    default: ''
  },
  modalWrapperClasses: {
    type: String,
    required: false,
    default: ''
  },
  modalHeaderClasses: {
    type: String,
    required: false,
    default: ''
  },
  isShowIconClose: {
    type: Boolean,
    required: false,
    default: true
  },
  isFullScreen: {
    type: Boolean,
    required: false,
    default: false
  },
  isClickBackdropClose: {
    type: Boolean,
    required: false,
    default: true
  },
  isFullHeader: {
    type: Boolean,
    required: false,
    default: false
  },
  headerTitle: {
    type: String,
    required: false,
    default: ''
  }
})
const { closeModal } = useModal()
const { isMobile, isWindows, isMacOS, isChrome } = useDevice()
const store = useModalStore()
const emits = defineEmits(['handleCloseModal'])
const isOpen = computed(() => {
  const modalData = store.getModal(props.modalId)
  return modalData.id
})
function handleCloseModal(): void {
  closeModal(props.modalId)
  emits('handleCloseModal')
}

const handleClickBackdrop = () => {
  if (props.isClickBackdropClose) {
    handleCloseModal()
  }
}
</script>
<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="isOpen"
        :id="modalId"
        class="modal show d-block"
        :class="[
          isMobile ? 'modal-custom-mobile' : 'modal-custom scrollbar-x',
          { 'modal-windows': isWindows },
          { 'modal-mac-os': isMacOS },
          { 'modal-chrome': isChrome },
          modalClass
        ]"
      >
        <div
          class="modal-dialog modal-dialog-centered"
          :class="[
            {
              'modal-dialog-bottom': isMobile
            },
            modalDialogClasses
          ]"
        >
          <div
            class="modal-content"
            :class="[
              {
                'modal-fullscreen': isFullScreen
              },
              modalContentClasses
            ]"
          >
            <div class="modal-body" :class="modalBodyClasses">
              <div class="modal-wrapper" :class="modalWrapperClasses">
                <div v-if="isShowIconClose" class="modal-header" :class="modalHeaderClasses">
                  <template v-if="isFullHeader">
                    <div class="header d-flex align-items-center">
                      <span class="icon-back icon-arrow-left" @click="handleCloseModal"></span>
                      <span class="header-title flex-grow-1">{{ headerTitle }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="ms-auto">
                      <div class="modal-icon-close" role="button" @click="handleCloseModal">
                        <span class="icon-close"></span>
                      </div>
                    </div>
                  </template>
                </div>
                <slot></slot>
              </div>
            </div>
          </div>
        </div>
        <div id="backdrop" class="modal-backdrop fade show" @click="handleClickBackdrop"></div>
      </div>
    </transition>
  </teleport>
</template>
<style scoped lang="scss">
@import '~scssAsset/components/common/modal.scss';
</style>
